// FILE: src/components/RecordOutcomeModal.js
//
// Phase E1 — "Record exam outcome" modal triggered from the Journey overlay.
//
// The form lets the learner record one of:
//   • passed (with optional PMI band + notes)
//   • failed (notes only — no shame in the data)
//   • withdrew (notes only)
//
// On submit we call certificationService.recordCertificationOutcome which
// invokes the SECURITY DEFINER RPC; on outcome='passed' that RPC also fires
// the certification_earned achievement atomically. Phase E3 will layer in
// Retake Mode for outcome='failed' and the Certification Story snapshot
// for outcome='passed'.
//
// Tone (§6.5 + §6.6 guardrails): no "try again," no "start over." For
// failed/withdrew the copy is plain and respectful — "Recorded. Thanks
// for telling us. You've got real exam experience now — that's an
// advantage."

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './RecordOutcomeModal.module.css';
import { getExamConfig } from '../../config/exams';
import { recordCertificationOutcome, OUTCOME } from '../../services/certificationService';

const PMI_SCORE_BANDS = [
  { value: '',  label: '— Not sure / not applicable —' },
  { value: 'A', label: 'Above Target' },
  { value: 'T', label: 'Target' },
  { value: 'B', label: 'Below Target' },
  { value: 'N', label: 'Needs Improvement' },
];

export default function RecordOutcomeModal({
  isOpen,
  onClose,
  onRecorded,
  activeCert,                // 'pmp' | 'cpmai' | 'pmi-acp'
}) {
  const [outcome, setOutcome]           = useState(OUTCOME.PASSED);
  const [actualDate, setActualDate]     = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scoreBand, setScoreBand]       = useState('');
  const [notes, setNotes]               = useState('');
  const [busy, setBusy]                 = useState(false);
  const [error, setError]               = useState(null);

  // Reset transient state when opened.
  useEffect(() => {
    if (!isOpen) return;
    const today = new Date().toISOString().slice(0, 10);
    setOutcome(OUTCOME.PASSED);
    setActualDate(today);
    setScheduledDate('');
    setScoreBand('');
    setNotes('');
    setError(null);
    setBusy(false);
  }, [isOpen]);

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const examLabel = activeCert ? (getExamConfig(activeCert)?.displayName || activeCert.toUpperCase()) : '—';
  const requiresActualDate    = outcome === OUTCOME.PASSED || outcome === OUTCOME.FAILED || outcome === OUTCOME.WITHDREW;
  const requiresScheduledDate = outcome === OUTCOME.SCHEDULED;
  const showScoreBand         = outcome === OUTCOME.PASSED || outcome === OUTCOME.FAILED;
  const today = new Date().toISOString().slice(0, 10);

  async function handleSubmit(e) {
    e?.preventDefault?.();
    if (!activeCert) { setError('Pick a certification first.'); return; }
    setBusy(true); setError(null);

    const params = {
      examId:    activeCert,
      outcome,
      scoreBand: showScoreBand && scoreBand ? scoreBand : null,
      notes:     notes ? notes.trim() : null,
    };
    if (requiresActualDate)    params.actualDate    = actualDate;
    if (requiresScheduledDate) params.scheduledDate = scheduledDate;

    const r = await recordCertificationOutcome(params);
    setBusy(false);
    if (!r.id) {
      setError(r.error || 'Could not record outcome — please try again.');
      return;
    }
    onRecorded?.({ id: r.id, outcome, examId: activeCert });
    onClose?.();
  }

  return createPortal(
    <div
      className={styles.backdrop}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="record-outcome-backdrop"
    >
      <div className={styles.panel} role="dialog" aria-modal="true" aria-labelledby="rom-title">
        <button
          type="button"
          className={styles.closeBtn}
          aria-label="Close record outcome"
          onClick={() => onClose?.()}
          data-testid="record-outcome-close"
        >
          ×
        </button>

        <p className={styles.eyebrow}>Your Certification Journey</p>
        <h2 id="rom-title" className={styles.title}>Record your {examLabel} outcome</h2>

        <p className={styles.lead}>
          Recording the outcome keeps your journey complete. We treat every entry the same — no badge of shame, no "starting over."
        </p>

        <form className={styles.form} onSubmit={handleSubmit}>
          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>Outcome</legend>
            <div className={styles.radioGroup} role="radiogroup">
              <Radio v={OUTCOME.PASSED}    label="Passed"    checked={outcome === OUTCOME.PASSED}    onChange={setOutcome} tid="outcome-passed"   busy={busy} />
              <Radio v={OUTCOME.FAILED}    label="Not yet"   checked={outcome === OUTCOME.FAILED}    onChange={setOutcome} tid="outcome-failed"   busy={busy} />
              <Radio v={OUTCOME.WITHDREW}  label="Withdrew"  checked={outcome === OUTCOME.WITHDREW}  onChange={setOutcome} tid="outcome-withdrew" busy={busy} />
              <Radio v={OUTCOME.SCHEDULED} label="Scheduled" checked={outcome === OUTCOME.SCHEDULED} onChange={setOutcome} tid="outcome-scheduled" busy={busy} />
            </div>
          </fieldset>

          {requiresActualDate && (
            <label className={styles.label}>
              Exam date
              <input
                type="date"
                value={actualDate}
                max={today}
                onChange={(e) => setActualDate(e.target.value)}
                required
                disabled={busy}
                data-testid="record-outcome-actual-date"
              />
            </label>
          )}

          {requiresScheduledDate && (
            <label className={styles.label}>
              Scheduled exam date
              <input
                type="date"
                value={scheduledDate}
                min={today}
                onChange={(e) => setScheduledDate(e.target.value)}
                required
                disabled={busy}
                data-testid="record-outcome-scheduled-date"
              />
            </label>
          )}

          {showScoreBand && (
            <label className={styles.label}>
              Score band <span className={styles.optional}>(optional)</span>
              <select
                value={scoreBand}
                onChange={(e) => setScoreBand(e.target.value)}
                disabled={busy}
                data-testid="record-outcome-band"
              >
                {PMI_SCORE_BANDS.map((b) => (
                  <option key={b.value} value={b.value}>{b.label}</option>
                ))}
              </select>
            </label>
          )}

          <label className={styles.label}>
            Notes <span className={styles.optional}>(optional)</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder={
                outcome === OUTCOME.PASSED
                  ? 'What clicked for you? Anything you want future-you to remember?'
                  : outcome === OUTCOME.FAILED
                    ? 'Anything you noticed — pacing, specific phases, fatigue?'
                    : 'Anything worth remembering.'
              }
              disabled={busy}
              data-testid="record-outcome-notes"
            />
          </label>

          {error && (
            <div className={styles.errorBox} role="alert" data-testid="record-outcome-error">
              {error}
            </div>
          )}

          {outcome === OUTCOME.FAILED && (
            <p className={styles.coachingNote}>
              Real exam experience is an advantage on retake. Your past sessions stay in your history; readiness will recalibrate from here.
            </p>
          )}

          <div className={styles.actionRow}>
            <button
              type="submit"
              className={styles.primaryBtn}
              disabled={busy || !activeCert}
              data-testid="record-outcome-submit"
            >
              {busy ? 'Recording…' : 'Record outcome'}
            </button>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={() => onClose?.()}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

function Radio({ v, label, checked, onChange, tid, busy }) {
  return (
    <label className={styles.radioLabel}>
      <input
        type="radio"
        name="outcome"
        value={v}
        checked={checked}
        onChange={() => onChange(v)}
        disabled={busy}
        data-testid={tid}
      />
      <span>{label}</span>
    </label>
  );
}
